import { useFormContext } from "react-hook-form";
import RegistrationHeader from "@/components/auth/registration/Header.tsx";
import { RegisterUserInput } from "@/types";
import OutlinedSelect from "@edusogno/inputs/outlined-select";
import AuthPageFooter from "@edusogno/layouts/auth-page-footer.js";
import { cn } from "@edusogno/lib/utils";
import LoadingButton from "@edusogno/primitives/loading-button";
import { useTranslation } from "react-i18next";
import RegistrationContainer from "../Container";
import RegistrationContent from "../Content";

interface ProfessionProps {
  className?: string;
  onNext: () => void;
}

/* eslint-disable react-refresh/only-export-components */
export const excludeProfessions = [
  "studente",
  "in-cerca-di-lavoro-disoccupato",
  "casalinga-o",
  "in-pensione",
];
/* eslint-enable react-refresh/only-export-components */

const RegistrationProfession = ({ className, onNext }: ProfessionProps) => {
  const {
    setValue,
    getValues,
    getFieldState,
    formState: { errors },
  } = useFormContext<RegisterUserInput>();
  const { t } = useTranslation();

  const professions = [
    {
      label: t("auth.registration.profession.student"),
      value: "studente",
    },
    {
      label: t("auth.registration.profession.intern"),
      value: "tirocinante-stagista",
    },
    {
      label: t("auth.registration.profession.worker"),
      value: "lavoro-stagionale-temporaneo",
    },
    {
      label: t("auth.registration.profession.unemployed"),
      value: "in-cerca-di-lavoro-disoccupato",
    },
    { label: t("auth.registration.profession.employee"), value: "impiegato" },
    {
      label: t("auth.registration.profession.freelancer"),
      value: "libero-professionista-autonomo",
    },
    {
      label: t("auth.registration.profession.entrepreneur"),
      value: "imprenditore-titolare-dazienda",
    },
    {
      label: t("auth.registration.profession.homemaker"),
      value: "casalinga-o",
    },
    { label: t("auth.registration.profession.retired"), value: "in-pensione" },
    {
      label: t("auth.registration.profession.others"),
      value: "others",
    },
  ];
  const sectors = [
    {
      label: t("auth.registration.sectors.agriculture"),
      value: "agricoltura",
    },
    {
      label: t("auth.registration.sectors.construction"),
      value: "costruzioni-e-immobiliare",
    },
    {
      label: t("auth.registration.sectors.education"),
      value: "educazione-e-formazione",
    },
    {
      label: t("auth.registration.sectors.finance"),
      value: "finanziario-e-assicurativo",
    },
    {
      label: t("auth.registration.sectors.government"),
      value: "governo-e-pubblica-amministrazion",
    },
    {
      label: t("auth.registration.sectors.it"),
      value: "it-e-tecnologia",
    },
    {
      label: t("auth.registration.sectors.manufacturing"),
      value: "manifatturiero-e-industriale",
    },
    {
      label: t("auth.registration.sectors.marketing"),
      value: "marketing-media-e-intrattenimento",
    },
    {
      label: t("auth.registration.sectors.ngo"),
      value: "non-profit-e-ong",
    },
    {
      label: t("auth.registration.sectors.services"),
      value: "servizi-sectorsali-e-consulenza",
    },
    {
      label: t("auth.registration.sectors.logistics"),
      value: "trasporti-e-logistica",
    },
    {
      label: t("auth.registration.sectors.others"),
      value: "others",
    },
  ];

  const isDisabled = () => {
    return (
      getFieldState("profession")?.invalid ||
      (!excludeProfessions.includes(getValues("profession")) &&
        !getValues("sector"))
    );
  };
  return (
    <>
      <RegistrationContainer className={cn("h-full", className)}>
        <RegistrationHeader
          title={t("auth.registration.profession_title")}
          subtitle={t("auth.registration.profession_subtitle")}
          logo={"profession"}
        />

        <RegistrationContent>
          <OutlinedSelect
            className={"w-full"}
            // size={"lg"}
            label={t("auth.labels.profession")}
            color={"grey"}
            defaultValue={getValues("profession")}
            options={professions}
            onValueChange={(value) => {
              setValue("profession", value, {
                shouldValidate: true,
              });
            }}
            error={Boolean(errors?.profession?.message)}
            helperText={errors?.profession?.message}
          />

          {!excludeProfessions.includes(getValues("profession")) && (
            <div className={"flex flex-col w-full "}>
              <OutlinedSelect
                className={"w-full"}
                // size={"lg"}
                label={t("auth.labels.sector")}
                color={"grey"}
                defaultValue={getValues("sector")}
                options={sectors}
                error={isDisabled()}
                onValueChange={(value) => {
                  setValue("sector", value, {
                    shouldValidate: true,
                  });
                }}
                helperText={errors?.sector?.message}
              />
            </div>
          )}
        </RegistrationContent>
      </RegistrationContainer>
      <AuthPageFooter>
        <LoadingButton
          size={"lg"}
          type="button"
          className="hidden w-full text-lg tracking-wide md:block"
          disabled={isDisabled()}
          onClick={() => onNext()}
        >
          {t("btns.next")}
        </LoadingButton>
        <LoadingButton
          size={"md"}
          type="button"
          className="block w-full text-lg tracking-wide md:hidden"
          disabled={isDisabled()}
          onClick={() => onNext()}
        >
          {t("btns.next")}
        </LoadingButton>
      </AuthPageFooter>
    </>
  );
};

export default RegistrationProfession;
